import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { toastError, toastSuccess } from '../components/FormComponents/Toasts'
import Campaign from '../models/Campaign'
import axiosService from '../utils/AxiosService'
import qs from 'qs'
import CampaignScenario from '../models/CampaignScenario'
import ProjectAsset from '../models/ProjectAsset'
import CampaignAuthentication from '../models/CampaignAuthentication'
import CampaignReport from '../models/CampaignReport'
import Influencer from '../models/Influencer'

export const CampaignContext = React.createContext({})

export default observer(function CampaignProvider(props) {
  const { id } = useParams()
  const location = useLocation()

  // Extract campaign_authentication_id from URL
  const getCampaignAuthIdFromUrl = () => {
    const match = location.pathname.match(/campaign_authentications\/(\d+)/)
    return match ? match[1] : null
  }

  const campaignState = useLocalObservable(() =>
    ({
      isLoading: true,
      campaign: new Campaign(),
      campaignAuthentications: [],
      campaignReport: new CampaignReport(),
      influencers: [],
      campaignScenarios: [],
      selectedScenario: null,
      selectedInfluencerAuth: null,
      collaborators: [],
      campaignContacts: [],
      campaignTags: [],
      campaignFolders: [],
      projectAssets: [],
      documents: [],
      documentSearch: {
        page: 1,
        file: '',
        type: null,
        campaign_folder_id: null,
        order: "file"
      }
    })
  )

  const getCampaign = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}`)
    .then(campaign => campaignState.campaign = new Campaign(campaign))
    .catch(err => console.log(err))
  }

  const setArchived = (archived) => {
    return axiosService.patch(`/api/project_manager/campaigns/${id}/${archived ? 'archive' : 'unarchive'}`)
      .then(campaign => {
        campaignState.campaign.archived_at = campaign.archived_at || null
        toastSuccess(archived ? 'Campaign has been archived' : 'Campaign has been restored')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const getAuthentications = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/authentications`)
      .then(authentications => {
        campaignState.campaignAuthentications = authentications.map(auth => new CampaignAuthentication(auth))
        const urlCampaignAuthId = getCampaignAuthIdFromUrl()
        if (urlCampaignAuthId) {
          campaignState.selectedInfluencerAuth = campaignState.campaignAuthentications.find( auth => auth.campaign_authentication_id === +urlCampaignAuthId )
        }
      })
      .catch(err => console.log(err))
  }

  const getCollaborators = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/collaborators`)
    .then(collaborators => campaignState.collaborators = collaborators)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getCampaignContacts = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_contacts`)
    .then(campaignContacts => campaignState.campaignContacts = campaignContacts)
    .catch(({ data: { error } }) => toastError(error))
  }

  const getCampaignTags = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_tags`)
    .then(campaignTags => campaignState.campaignTags = campaignTags)
    .catch(({ data: { error }}) => toastError(error))
  }

  const loadCampaignFolders = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_folders`)
    .then(campaign_folders => campaignState.campaignFolders = campaign_folders)
    .catch(({ data: { error } }) => toastError(error))
  }

  const loadSelectedScenario = (scenarios = campaignState.campaignScenarios) => {
    const sorted = [...scenarios].sort((a, b) => Number(a.id) - Number(b.id))
    campaignState.selectedScenario = sorted[0] || null
  }

  const loadCampaignScenarios = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_scenarios`)
    .then((campaign_scenarios) => {
      const models = campaign_scenarios.map(cs => new CampaignScenario(cs))
      models.sort((a, b) => Number(a.id) - Number(b.id))
      campaignState.campaignScenarios = models
      loadSelectedScenario(campaignState.campaignScenarios)
    })
    .catch((error) => {
      console.error('Error loading campaign scenarios:', error);
      toastError(error?.data?.error || 'Failed to load campaign scenarios');
    });
  }

  const createCampaignScenario = (name) => {
    return axiosService.post(`/api/project_manager/campaigns/${id}/campaign_scenarios`, { campaign_scenario: { name } })
      .then(campaign_scenario => {
        const model = new CampaignScenario(campaign_scenario)
        campaignState.campaignScenarios.push(model)
        return model
      })
      .catch(({ data: { error } }) => { toastError(error); throw error })
  }

  const duplicateCampaignScenario = (scenarioId) => {
    return axiosService.post(`/api/project_manager/campaigns/${id}/campaign_scenarios/${scenarioId}/duplicate`)
      .then(campaign_scenario => {
        const model = new CampaignScenario(campaign_scenario)
        campaignState.campaignScenarios.push(model)
        return model
      })
      .catch(({ data: { error } }) => { toastError(error); throw error })
  }

  const renameCampaignScenario = (scenarioId, name) => {
    return axiosService.patch(`/api/project_manager/campaigns/${id}/campaign_scenarios/${scenarioId}`, { campaign_scenario: { name } })
      .then(updated => {
        const scenario = campaignState.campaignScenarios.find(s => Number(s.id) === Number(scenarioId))
        if (scenario) scenario.name = updated.name
        return updated
      })
      .catch(({ data: { error } }) => { toastError(error); throw error })
  }

  const deleteCampaignScenario = (scenarioId) => {
    return axiosService.delete(`/api/project_manager/campaigns/${id}/campaign_scenarios/${scenarioId}`)
      .then(() => {
        campaignState.campaignScenarios = campaignState.campaignScenarios.filter(s => Number(s.id) !== Number(scenarioId))
        loadSelectedScenario(campaignState.campaignScenarios)
      })
      .catch(({ data: { error } }) => { toastError(error); throw error })
  }

  const toggleCampaignScenarioActive = (scenarioId) => {
    const scenario = campaignState.campaignScenarios.find(s => Number(s.id) === Number(scenarioId))
    if (!scenario) return Promise.resolve()
    return axiosService.patch(`/api/project_manager/campaigns/${id}/campaign_scenarios/${scenarioId}`, { campaign_scenario: { active: !scenario.active } })
      .then(updated => {
        scenario.active = !!updated.active
        return scenario
      })
      .catch(({ data: { error } }) => { toastError(error); throw error })
  }

  const loadInfluencers = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/influencers`)
    .then(influencers => campaignState.influencers = influencers.map(influencer => new Influencer(influencer)))
    .catch(({ data: { error } }) => toastError(error))
  }

  const loadProjectAssets = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/project_assets`)
    .then(projectAssets => campaignState.projectAssets = projectAssets.map(projectAsset => new ProjectAsset(projectAsset)))
    .catch(({ data: { error } }) => toastError(error))
  }

  const loadCampaignReport = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/campaign_report`)
    .then(campaignReport => campaignState.campaignReport = new CampaignReport(campaignReport))
    .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    campaignState.isLoading = true;
    Promise.all([
      loadInfluencers(),
      loadCampaignScenarios(),
      loadProjectAssets(),
      loadCampaignFolders(),
      getAuthentications(),
      getCampaign(),
      getCollaborators(),
      getCampaignContacts(),
      getCampaignTags(),
      loadCampaignReport()
    ]).finally(() => {
      campaignState.isLoading = false
    })
  }, [id])

  useEffect(() => {
    if(campaignState.documentSearch.page === 1) return
    loadMoreDocuments()
  }, [campaignState.documentSearch.page])

  useEffect(() => {
    campaignState.documentSearch.page = 1
    loadDocuments()
  }, [
    campaignState.documentSearch.file,
    campaignState.documentSearch.type,
    campaignState.documentSearch.campaign_folder_id,
    campaignState.documentSearch.order,
  ])

  const loadDocuments = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/documents.json?${qs.stringify({ search: getParams() }, { arrayFormat: 'brackets' })}`)
    .then(documents => campaignState.documents = documents)
    .catch(({ data: { error } }) => toastError(error))
  }

  const loadMoreDocuments = () => {
    axiosService.get(`/api/project_manager/campaigns/${id}/documents.json?${qs.stringify({ search: getParams() }, { arrayFormat: 'brackets' })}`)
    .then(documents => campaignState.documents.push(...documents))
    .catch(({ data: { error } }) => toastError(error))
  }

  const getParams = () => {
    return {
      page: campaignState.documentSearch.page,
      file: campaignState.documentSearch.file,
      type: campaignState.documentSearch.type,
      campaign_folder_id: campaignState.documentSearch.campaign_folder_id,
      order: campaignState.documentSearch.order,
    }
  }

  // Add a effect to update the selected influencer when the URL changes
  useEffect(() => {
    const urlCampaignAuthId = getCampaignAuthIdFromUrl()
    if (urlCampaignAuthId && campaignState.campaignAuthentications.length > 0) {
      campaignState.selectedInfluencerAuth = campaignState.campaignAuthentications.find( auth => auth.campaign_authentication_id === +urlCampaignAuthId)
    }
  }, [location.pathname])

  return (
    <CampaignContext.Provider
      value={{
        campaignState,
        loadDocuments,
        getAuthentications,
        documents: campaignState.documents,
        campaignReport: campaignState.campaignReport,
        campaignFolders: campaignState.campaignFolders,
        documentSearch: campaignState.documentSearch,
        campaign: campaignState.campaign,
        isArchived: !!campaignState.campaign.archived_at,
        archiveCampaign: () => setArchived(true),
        unarchiveCampaign: () => setArchived(false),
        authentications: campaignState.campaignAuthentications,
        collaborators: campaignState.collaborators,
        campaignTags: campaignState.campaignTags,
        campaignContacts: campaignState.campaignContacts,
        influencers: campaignState.influencers,
        projectAssets: campaignState.projectAssets,
        campaignScenarios: campaignState.campaignScenarios,
        primaryScenarioId: campaignState.campaignScenarios[0]?.id ?? null,
        selectedScenario: campaignState.selectedScenario,
        selectedInfluencerAuth: campaignState.selectedInfluencerAuth,
        isLoading: campaignState.isLoading,
        loadInfluencers,
        loadCampaignScenarios,
        loadSelectedScenario,
        createCampaignScenario,
        duplicateCampaignScenario,
        renameCampaignScenario,
        deleteCampaignScenario,
        toggleCampaignScenarioActive,
      }}
    >
      {props.children}
    </CampaignContext.Provider>
  )
})
